import { Permission, type GrantablePermission } from '@todo/shared';
import { Checkbox } from './ui';

/**
 * The "Lottery access" block of the People form - Super Admin only (the
 * form decides whether to render it).
 *
 * Controlled: `value` is the full list of grantable permissions the person
 * should hold. Upload needs View, so Upload is disabled until View is
 * ticked, and unticking View clears Upload too - the same rule the API
 * enforces (services/permission.service.ts), which is the actual control.
 */

interface LotteryAccessFieldsProps {
  /** null while the current grants are still loading. */
  value: GrantablePermission[] | null;
  onChange: (next: GrantablePermission[]) => void;
  /** Shown instead of the checkboxes when the current grants could not load. */
  error?: string | null;
  disabled?: boolean;
}

export function LotteryAccessFields({
  value,
  onChange,
  error,
  disabled,
}: LotteryAccessFieldsProps) {
  const canView = value?.includes(Permission.LOTTERY_VIEW) ?? false;
  const canUpload = value?.includes(Permission.LOTTERY_UPLOAD) ?? false;

  function setView(checked: boolean) {
    // Revoking View revokes Upload with it.
    onChange(checked ? [Permission.LOTTERY_VIEW, ...(canUpload ? [Permission.LOTTERY_UPLOAD] : [])] : []);
  }

  function setUpload(checked: boolean) {
    onChange(checked ? [Permission.LOTTERY_VIEW, Permission.LOTTERY_UPLOAD] : [Permission.LOTTERY_VIEW]);
  }

  return (
    <fieldset className="rounded-lg px-3 py-3 ring-1 ring-slate-200">
      <legend className="px-1 text-sm font-medium text-slate-700">Lottery access</legend>

      {error ? (
        <p role="alert" className="text-xs text-red-600">
          {error}
        </p>
      ) : value === null ? (
        <p className="text-xs text-slate-500">Loading current access...</p>
      ) : (
        <div className="space-y-3">
          <Checkbox
            label="View lottery data and reports"
            checked={canView}
            onChange={setView}
            disabled={disabled}
          />
          <Checkbox
            label="Upload daily files"
            checked={canUpload}
            onChange={setUpload}
            disabled={disabled || !canView}
            hint={canView ? undefined : 'Needs "View lottery data and reports" first.'}
          />
          <p className="text-xs text-slate-500">
            Takes effect on their next request. An invited person gets it once
            they have signed in.
          </p>
        </div>
      )}
    </fieldset>
  );
}
